export type FormatCellsTab = 'number' | 'alignment' | 'font' | 'border' | 'fill' | 'protection'

export type FormatCellsStyleSectionKey =
  | 'numberFormat'
  | 'font'
  | 'fill'
  | 'border'
  | 'alignment'
  | 'protection'

export type FormatCellsCellFormat = {
  [key: string]: unknown
  numberFormat?: Record<string, unknown>
  font?: Record<string, unknown>
  fill?: Record<string, unknown>
  border?: Record<string, unknown>
  alignment?: Record<string, unknown>
  protection?: Record<string, unknown>
  bold?: boolean
  italic?: boolean
  underline?: boolean | string
  strikethrough?: boolean
  align?: string
  verticalAlign?: string
  wrapText?: boolean
  shrinkToFit?: boolean
  indent?: number
  textRotation?: number
  fontSize?: number
  fontFamily?: string
  textColor?: string
  backgroundColor?: string
}

export type FormatCellsPreviewStyle = {
  color: string
  backgroundColor: string
  fontFamily: string
  fontSize: number
  fontWeight: number
  fontStyle: 'italic' | 'normal'
  textDecoration: string
  textAlign: string
  direction: string
  borderTop?: string
  borderRight?: string
  borderBottom?: string
  borderLeft?: string
  border?: string
}

export type FormatCellsBorderPreset =
  | 'none'
  | 'outline'
  | 'inside'
  | 'all'
  | 'thickOutline'
  | 'bottom'
  | 'topBottom'
  | 'topThickBottom'
  | 'topDoubleBottom'

export type RibbonFormatDescriptor = {
  id: string
  label: string
  format: Partial<FormatCellsCellFormat>
}

export type RibbonColorDescriptor = {
  id: string
  label: string
  color: string
}

export type RibbonTableStyleDescriptor = {
  id: string
  label: string
  tableStyleId: string
  style: {
    header: Partial<FormatCellsCellFormat>
    body: Partial<FormatCellsCellFormat>
    banded: Partial<FormatCellsCellFormat>
  }
}

export type RibbonMenuActionId =
  | 'set-font-family'
  | 'set-font-size'
  | 'set-font-color'
  | 'set-fill-color'
  | 'set-number-format'
  | 'set-border-format'
  | 'set-cell-style'
  | 'set-table-style'
  | 'no-fill'
  | 'open-format-cells'
  | 'autofit-column-width'
  | 'autofit-row-height'
  | 'clear-formats'
  | 'sort-ascending'
  | 'sort-descending'
  | 'filter-selection'
  | 'clear-filter'
  | 'merge-center'
  | 'merge-across'
  | 'unmerge'

export type RibbonMenuDescriptor = {
  id: string
  label: string
  title?: string
  action: RibbonMenuActionId
  selected?: boolean
  value?: string | number
}

export type RibbonFormatCommandId =
  | RibbonTextStyleCommandId
  | RibbonHorizontalAlignmentCommandId
  | RibbonIndentCommandId
  | RibbonDecimalCommandId
  | RibbonNumberCommandId
  | 'borders'
  | 'wrap-text'
  | 'merge-center'
  | 'clear-formats'
  | 'format-as-table'
  | 'format-cells'

export type RibbonNumberCommandId =
  | 'number-format'
  | 'accounting-format'
  | 'percent-style'
  | 'comma-style'

export type RibbonTextStyleCommandId = 'bold' | 'italic' | 'underline'

export type RibbonHorizontalAlignment = 'left' | 'center' | 'right'

export type RibbonHorizontalAlignmentCommandId = 'align-left' | 'align-center' | 'align-right'

export type RibbonIndentCommandId = 'decrease-indent' | 'increase-indent'

export type RibbonDecimalCommandId = 'decrease-decimal' | 'increase-decimal'

export type RibbonDefaultMenuCommandId =
  | 'font-family'
  | 'font-size'
  | 'font-color'
  | 'fill-color'
  | 'number-format'
  | 'borders'
  | 'cell-styles'
  | 'format-as-table'
  | 'format-cells'
  | 'sort-filter'
  | 'merge-center'

export type RibbonFormatActionPlan =
  | { action: 'apply-format'; format: Partial<FormatCellsCellFormat> }
  | { action: 'merge-center'; format: Partial<FormatCellsCellFormat> }
  | { action: 'clear-formats' }
  | { action: 'apply-table-style'; styleId: string; style: RibbonTableStyleDescriptor['style'] }
  | { action: 'open-format-cells' }
  | { action: 'autofit-column-width' }
  | { action: 'autofit-row-height' }
  | { action: 'sort-selection'; descending: boolean }
  | { action: 'apply-autofilter' }
  | { action: 'clear-autofilter' }
  | { action: 'merge-across' }
  | { action: 'unmerge' }

export type RibbonColorInputKind = 'font' | 'fill'

export const FORMAT_CELLS_FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 22, 24, 28, 32, 36]

export const RIBBON_FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36]

export const FORMAT_CELLS_TABS: FormatCellsTab[] = ['number', 'alignment', 'font', 'border', 'fill', 'protection']

export const NUMBER_FORMAT_CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'accounting', label: 'Accounting' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'fraction', label: 'Fraction' },
  { value: 'scientific', label: 'Scientific' },
  { value: 'text', label: 'Text' },
  { value: 'special', label: 'Special' },
  { value: 'custom', label: 'Custom' },
] as const

export const NUMBER_FORMAT_LOCALE_OPTIONS = [
  { value: 'en-US', label: 'English (United States)' },
  { value: 'en-GB', label: 'English (United Kingdom)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'ja-JP', label: 'Japanese (Japan)' },
] as const

export const NUMBER_FORMAT_CURRENCY_SYMBOL_OPTIONS = [
  { value: '$', label: '$' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'JPY', label: 'JPY' },
] as const

export const NUMBER_FORMAT_NEGATIVE_OPTIONS = [
  { value: 'minus', label: '-1234.10' },
  { value: 'red', label: '1234.10 red' },
  { value: 'parentheses', label: '(1234.10)' },
  { value: 'redParentheses', label: '(1234.10) red' },
] as const

export const SPECIAL_NUMBER_FORMATS = [
  { code: '00000', label: 'Zip Code' },
  { code: '00000-0000', label: 'Zip Code + 4' },
  { code: '(000) 000-0000', label: 'Phone Number' },
  { code: '000-00-0000', label: 'Social Security Number' },
] as const

export const RIBBON_COLOR_PALETTE: RibbonColorDescriptor[] = [
  { id: 'automatic', label: 'Automatic', color: '#111827' },
  { id: 'white', label: 'White', color: '#ffffff' },
  { id: 'black', label: 'Black', color: '#000000' },
  { id: 'red', label: 'Red', color: '#c00000' },
  { id: 'orange', label: 'Orange', color: '#f4b183' },
  { id: 'yellow', label: 'Yellow', color: '#ffd966' },
  { id: 'green', label: 'Green', color: '#70ad47' },
  { id: 'blue', label: 'Blue', color: '#4472c4' },
  { id: 'purple', label: 'Purple', color: '#7030a0' },
]

export const RIBBON_NUMBER_FORMAT_PRESETS: RibbonFormatDescriptor[] = [
  { id: 'general', label: 'General', format: { numberFormat: { category: 'general' } } },
  { id: 'number', label: 'Number', format: { numberFormat: { category: 'number', decimalPlaces: 2, useThousandsSeparator: false } } },
  { id: 'currency', label: 'Currency', format: { numberFormat: { category: 'currency', decimalPlaces: 2, currencySymbol: '$' } } },
  { id: 'accounting', label: 'Accounting', format: { numberFormat: { category: 'accounting', decimalPlaces: 2, currencySymbol: '$' } } },
  { id: 'percentage', label: 'Percentage', format: { numberFormat: { category: 'percentage', decimalPlaces: 2 } } },
  { id: 'comma', label: 'Comma Style', format: { numberFormat: { category: 'number', decimalPlaces: 2, useThousandsSeparator: true } } },
  { id: 'date', label: 'Short Date', format: { numberFormat: { category: 'date', code: 'm/d/yyyy' } } },
  { id: 'time', label: 'Time', format: { numberFormat: { category: 'time', code: 'h:mm AM/PM' } } },
  { id: 'fraction', label: 'Fraction', format: { numberFormat: { category: 'fraction', code: '# ?/?' } } },
  { id: 'scientific', label: 'Scientific', format: { numberFormat: { category: 'scientific', decimalPlaces: 2 } } },
  { id: 'special', label: 'Special', format: { numberFormat: { category: 'special', code: '00000' } } },
  { id: 'text', label: 'Text', format: { numberFormat: { category: 'text' } } },
]

export const RIBBON_BORDER_FORMAT_PRESETS: RibbonFormatDescriptor[] = [
  {
    id: 'bottom',
    label: 'Bottom Border',
    format: { border: { bottom: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'top',
    label: 'Top Border',
    format: { border: { top: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'left',
    label: 'Left Border',
    format: { border: { left: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'right',
    label: 'Right Border',
    format: { border: { right: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'all',
    label: 'All Borders',
    format: {
      border: {
        top: { style: 'thin', color: '#1f2937' },
        right: { style: 'thin', color: '#1f2937' },
        bottom: { style: 'thin', color: '#1f2937' },
        left: { style: 'thin', color: '#1f2937' },
        insideHorizontal: { style: 'thin', color: '#1f2937' },
        insideVertical: { style: 'thin', color: '#1f2937' },
      },
    },
  },
  {
    id: 'outside',
    label: 'Outside Borders',
    format: {
      border: {
        top: { style: 'thin', color: '#1f2937' },
        right: { style: 'thin', color: '#1f2937' },
        bottom: { style: 'thin', color: '#1f2937' },
        left: { style: 'thin', color: '#1f2937' },
      },
    },
  },
  {
    id: 'thick-outside',
    label: 'Thick Outside Borders',
    format: {
      border: {
        top: { style: 'thick', color: '#1f2937' },
        right: { style: 'thick', color: '#1f2937' },
        bottom: { style: 'thick', color: '#1f2937' },
        left: { style: 'thick', color: '#1f2937' },
      },
    },
  },
  {
    id: 'inside',
    label: 'Inside Borders',
    format: {
      border: {
        insideHorizontal: { style: 'thin', color: '#1f2937' },
        insideVertical: { style: 'thin', color: '#1f2937' },
      },
    },
  },
  {
    id: 'diagonal',
    label: 'Diagonal Border',
    format: { border: { diagonal: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'diagonal-up',
    label: 'Diagonal Up Border',
    format: { border: { diagonalUp: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'diagonal-down',
    label: 'Diagonal Down Border',
    format: { border: { diagonalDown: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'top-bottom',
    label: 'Top and Bottom Border',
    format: { border: { top: { style: 'thin', color: '#1f2937' }, bottom: { style: 'thin', color: '#1f2937' } } },
  },
  {
    id: 'top-thick-bottom',
    label: 'Top and Thick Bottom Border',
    format: { border: { top: { style: 'thin', color: '#1f2937' }, bottom: { style: 'thick', color: '#1f2937' } } },
  },
  {
    id: 'top-double-bottom',
    label: 'Top and Double Bottom Border',
    format: { border: { top: { style: 'thin', color: '#1f2937' }, bottom: { style: 'double', color: '#1f2937' } } },
  },
  {
    id: 'none',
    label: 'No Border',
    format: {
      border: {
        top: { style: 'none' },
        right: { style: 'none' },
        bottom: { style: 'none' },
        left: { style: 'none' },
        insideHorizontal: { style: 'none' },
        insideVertical: { style: 'none' },
        diagonal: { style: 'none' },
        diagonalUp: { style: 'none' },
        diagonalDown: { style: 'none' },
      },
    },
  },
]

export const RIBBON_CELL_STYLE_PRESETS: RibbonFormatDescriptor[] = [
  {
    id: 'normal',
    label: 'Normal',
    format: {
      font: { bold: false, italic: false, color: '#111827' },
      fill: { type: 'none' },
      bold: false,
      italic: false,
      textColor: '#111827',
      backgroundColor: '#ffffff',
    },
  },
  {
    id: 'good',
    label: 'Good',
    format: {
      fill: { type: 'solid', foregroundColor: '#c6efce' },
      font: { color: '#006100', bold: true },
      backgroundColor: '#c6efce',
      textColor: '#006100',
      bold: true,
    },
  },
  {
    id: 'bad',
    label: 'Bad',
    format: {
      fill: { type: 'solid', foregroundColor: '#ffc7ce' },
      font: { color: '#9c0006', bold: true },
      backgroundColor: '#ffc7ce',
      textColor: '#9c0006',
      bold: true,
    },
  },
  {
    id: 'neutral',
    label: 'Neutral',
    format: {
      fill: { type: 'solid', foregroundColor: '#ffeb9c' },
      font: { color: '#9c6500', bold: true },
      backgroundColor: '#ffeb9c',
      textColor: '#9c6500',
      bold: true,
    },
  },
  {
    id: 'heading',
    label: 'Heading 1',
    format: {
      font: { bold: true, color: '#ffffff' },
      fill: { type: 'solid', foregroundColor: '#107c41' },
      bold: true,
      textColor: '#ffffff',
      backgroundColor: '#107c41',
    },
  },
  {
    id: 'title',
    label: 'Title',
    format: {
      font: { bold: true, size: 18, color: '#1f2937' },
      border: { bottom: { style: 'medium', color: '#107c41' } },
      bold: true,
      fontSize: 18,
      textColor: '#1f2937',
    },
  },
  {
    id: 'total',
    label: 'Total',
    format: {
      font: { bold: true },
      border: { top: { style: 'double', color: '#1f2937' } },
      bold: true,
    },
  },
  {
    id: 'calculation',
    label: 'Calculation',
    format: {
      fill: { type: 'solid', foregroundColor: '#fce4d6' },
      border: { left: { style: 'medium', color: '#c55a11' } },
      backgroundColor: '#fce4d6',
    },
  },
  {
    id: 'check-cell',
    label: 'Check Cell',
    format: {
      fill: { type: 'solid', foregroundColor: '#d9ead3' },
      font: { color: '#274e13', bold: true },
      border: { bottom: { style: 'thin', color: '#6aa84f' } },
      backgroundColor: '#d9ead3',
      textColor: '#274e13',
      bold: true,
    },
  },
  {
    id: 'explanatory-text',
    label: 'Explanatory Text',
    format: {
      font: { italic: true, color: '#666666' },
      italic: true,
      textColor: '#666666',
    },
  },
  {
    id: 'input',
    label: 'Input',
    format: {
      fill: { type: 'solid', foregroundColor: '#fff2cc' },
      border: { bottom: { style: 'thin', color: '#bf9000' } },
      backgroundColor: '#fff2cc',
    },
  },
  {
    id: 'linked-cell',
    label: 'Linked Cell',
    format: {
      font: { color: '#0563c1', underline: 'single' },
      textColor: '#0563c1',
      underline: 'single',
    },
  },
  {
    id: 'note',
    label: 'Note',
    format: {
      fill: { type: 'solid', foregroundColor: '#fde9d9' },
      font: { color: '#7f6000' },
      backgroundColor: '#fde9d9',
      textColor: '#7f6000',
    },
  },
  {
    id: 'output',
    label: 'Output',
    format: {
      fill: { type: 'solid', foregroundColor: '#ddebf7' },
      border: { bottom: { style: 'thin', color: '#5b9bd5' } },
      backgroundColor: '#ddebf7',
    },
  },
  {
    id: 'warning-text',
    label: 'Warning Text',
    format: {
      font: { bold: true, color: '#c00000' },
      bold: true,
      textColor: '#c00000',
    },
  },
]

export const RIBBON_TABLE_STYLE_PRESETS: RibbonTableStyleDescriptor[] = [
  {
    id: 'green',
    label: 'Green Table Style',
    tableStyleId: 'TableStyleMedium2',
    style: {
      header: {
        fill: { type: 'solid', foregroundColor: '#107c41' },
        font: { bold: true, color: '#ffffff' },
        backgroundColor: '#107c41',
        textColor: '#ffffff',
        bold: true,
      },
      body: {
        border: { bottom: { style: 'thin', color: '#d0d7de' } },
      },
      banded: {
        fill: { type: 'solid', foregroundColor: '#e2f0d9' },
        backgroundColor: '#e2f0d9',
      },
    },
  },
  {
    id: 'blue',
    label: 'Blue Table Style',
    tableStyleId: 'TableStyleMedium4',
    style: {
      header: {
        fill: { type: 'solid', foregroundColor: '#1f4e78' },
        font: { bold: true, color: '#ffffff' },
        backgroundColor: '#1f4e78',
        textColor: '#ffffff',
        bold: true,
      },
      body: {
        border: { bottom: { style: 'thin', color: '#d0d7de' } },
      },
      banded: {
        fill: { type: 'solid', foregroundColor: '#ddebf7' },
        backgroundColor: '#ddebf7',
      },
    },
  },
]

export const RIBBON_FORMAT_CELLS_MENU: RibbonMenuDescriptor[] = [
  {
    id: 'format-cells-dialog',
    label: 'Format Cells...',
    title: 'Open the Format Cells dialog',
    action: 'open-format-cells',
  },
  {
    id: 'autofit-column-width',
    label: 'AutoFit Column Width',
    title: 'Resize selected columns to fit their formatted contents',
    action: 'autofit-column-width',
  },
  {
    id: 'autofit-row-height',
    label: 'AutoFit Row Height',
    title: 'Resize selected rows to fit wrapped, rotated, and multi-line text',
    action: 'autofit-row-height',
  },
  {
    id: 'clear-formats',
    label: 'Clear Formats',
    title: 'Clear formatting from the selected cells',
    action: 'clear-formats',
  },
]

export const RIBBON_SORT_FILTER_MENU: RibbonMenuDescriptor[] = [
  {
    id: 'sort-ascending',
    label: 'Sort Ascending',
    title: 'Sort selected range ascending by the first selected column',
    action: 'sort-ascending',
  },
  {
    id: 'sort-descending',
    label: 'Sort Descending',
    title: 'Sort selected range descending by the first selected column',
    action: 'sort-descending',
  },
  {
    id: 'filter-selection',
    label: 'Filter',
    title: 'Apply AutoFilter to the selected range',
    action: 'filter-selection',
  },
  {
    id: 'clear-filter',
    label: 'Clear Filter',
    title: 'Clear the active AutoFilter for this sheet',
    action: 'clear-filter',
  },
]

export const RIBBON_MERGE_MENU: RibbonMenuDescriptor[] = [
  {
    id: 'merge-center',
    label: 'Merge & Center',
    action: 'merge-center',
  },
  {
    id: 'merge-across',
    label: 'Merge Across',
    action: 'merge-across',
  },
  {
    id: 'unmerge',
    label: 'Unmerge Cells',
    action: 'unmerge',
  },
]

export function buildRibbonFontFamilyMenuDescriptors(
  currentFamily: string,
  fontFamilyOptions: Array<{ value: string; label: string }>,
): RibbonMenuDescriptor[] {
  const normalized = String(currentFamily || '').trim()
  return [
    {
      id: 'default',
      label: 'Default',
      action: 'set-font-family',
      selected: normalized.length === 0,
    },
    ...fontFamilyOptions.map((option) => ({
      id: option.value,
      label: option.label,
      action: 'set-font-family' as const,
      value: option.value,
      selected: normalized === option.value,
    })),
  ]
}

export function buildRibbonFontSizeMenuDescriptors(currentSize: number): RibbonMenuDescriptor[] {
  return RIBBON_FONT_SIZE_OPTIONS.map((size) => ({
    id: String(size),
    label: String(size),
    action: 'set-font-size',
    value: size,
    selected: currentSize === size,
  }))
}

export function buildRibbonFontColorMenuDescriptors(currentColor: string): RibbonMenuDescriptor[] {
  const normalized = toFormatCellsColorInputValue(currentColor, '#111827').toLowerCase()
  return RIBBON_COLOR_PALETTE.map((entry) => ({
    id: entry.id,
    label: entry.label,
    action: 'set-font-color',
    value: entry.color,
    selected: normalized === entry.color.toLowerCase(),
  }))
}

export function buildRibbonFillColorMenuDescriptors(currentColor: string): RibbonMenuDescriptor[] {
  const normalized = toFormatCellsColorInputValue(currentColor, '#fff2cc').toLowerCase()
  return [
    {
      id: 'no-fill',
      label: 'No Fill',
      action: 'no-fill' as const,
      selected: false,
    },
    ...RIBBON_COLOR_PALETTE.map((entry) => ({
      id: entry.id,
      label: entry.label,
      action: 'set-fill-color' as const,
      value: entry.color,
      selected: normalized === entry.color.toLowerCase(),
    })),
  ]
}

export function buildRibbonNumberFormatMenuDescriptors(
  currentNumberFormat?: Record<string, unknown>,
): RibbonMenuDescriptor[] {
  const currentCategory = String(currentNumberFormat?.category ?? 'general').toLowerCase()
  return RIBBON_NUMBER_FORMAT_PRESETS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    action: 'set-number-format',
    value: entry.id,
    selected: entry.id === currentCategory || (
      entry.id === 'comma' &&
      currentCategory === 'number' &&
      currentNumberFormat?.useThousandsSeparator === true
    ),
  }))
}

export function buildRibbonBorderMenuDescriptors(): RibbonMenuDescriptor[] {
  return RIBBON_BORDER_FORMAT_PRESETS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    action: 'set-border-format',
    value: entry.id,
  }))
}

export function buildRibbonCellStyleMenuDescriptors(): RibbonMenuDescriptor[] {
  return RIBBON_CELL_STYLE_PRESETS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    action: 'set-cell-style',
    value: entry.id,
  }))
}

export function buildRibbonTableStyleMenuDescriptors(): RibbonMenuDescriptor[] {
  return RIBBON_TABLE_STYLE_PRESETS.map((entry) => ({
    id: entry.id,
    label: entry.label,
    action: 'set-table-style',
    value: entry.id,
  }))
}

export function currentRibbonFontFamily(format?: FormatCellsCellFormat): string {
  return String(format?.fontFamily ?? formatCellsSection(format ?? {}, 'font').family ?? '').trim()
}

export function currentRibbonFontSize(format?: FormatCellsCellFormat): number {
  return Number(format?.fontSize ?? formatCellsSection(format ?? {}, 'font').size ?? 0) || 0
}

export function currentRibbonTextColor(format?: FormatCellsCellFormat): string | undefined {
  const font = formatCellsSection(format ?? {}, 'font')
  return typeof format?.textColor === 'string'
    ? format.textColor
    : typeof font.color === 'string'
      ? String(font.color)
      : undefined
}

export function currentRibbonFillColor(format?: FormatCellsCellFormat): string | undefined {
  const fill = formatCellsSection(format ?? {}, 'fill')
  return typeof format?.backgroundColor === 'string'
    ? format.backgroundColor
    : typeof fill.foregroundColor === 'string'
      ? String(fill.foregroundColor)
      : typeof fill.backgroundColor === 'string'
        ? String(fill.backgroundColor)
        : undefined
}

export function buildRibbonDefaultMenuDescriptors(args: {
  id: RibbonDefaultMenuCommandId
  currentFormat?: FormatCellsCellFormat
  fontFamilyOptions?: Array<{ value: string; label: string }>
}): RibbonMenuDescriptor[] {
  switch (args.id) {
    case 'font-family':
      return buildRibbonFontFamilyMenuDescriptors(
        currentRibbonFontFamily(args.currentFormat),
        args.fontFamilyOptions ?? [],
      )
    case 'font-size':
      return buildRibbonFontSizeMenuDescriptors(currentRibbonFontSize(args.currentFormat))
    case 'font-color':
      return buildRibbonFontColorMenuDescriptors(
        toFormatCellsColorInputValue(currentRibbonTextColor(args.currentFormat), '#111827'),
      )
    case 'fill-color':
      return buildRibbonFillColorMenuDescriptors(
        toFormatCellsColorInputValue(currentRibbonFillColor(args.currentFormat), '#fff2cc'),
      )
    case 'number-format':
      return buildRibbonNumberFormatMenuDescriptors(args.currentFormat?.numberFormat)
    case 'borders':
      return buildRibbonBorderMenuDescriptors()
    case 'cell-styles':
      return buildRibbonCellStyleMenuDescriptors()
    case 'format-as-table':
      return buildRibbonTableStyleMenuDescriptors()
    case 'format-cells':
      return RIBBON_FORMAT_CELLS_MENU
    case 'sort-filter':
      return RIBBON_SORT_FILTER_MENU
    case 'merge-center':
      return RIBBON_MERGE_MENU
  }
}

export const NUMBER_FORMAT_TYPE_PRESETS: Record<string, Array<{ code: string; label: string }>> = {
  date: [
    { code: 'm/d/yyyy', label: '3/14/2012' },
    { code: 'd-mmm-yy', label: '14-Mar-12' },
    { code: 'mmmm d, yyyy', label: 'March 14, 2012' },
  ],
  time: [
    { code: 'h:mm AM/PM', label: '1:30 PM' },
    { code: 'h:mm:ss AM/PM', label: '1:30:55 PM' },
    { code: 'h:mm', label: '13:30' },
    { code: 'h:mm:ss', label: '13:30:55' },
    { code: '[h]:mm:ss', label: '37:30:55' },
  ],
  fraction: [
    { code: '# ?/?', label: 'Up to one digit' },
    { code: '# ??/??', label: 'Up to two digits' },
    { code: '# ???/???', label: 'Up to three digits' },
  ],
}

export function ribbonFontColorFormat(color: string): Partial<FormatCellsCellFormat> {
  return { textColor: color, font: { color } }
}

export function ribbonFillColorFormat(color: string): Partial<FormatCellsCellFormat> {
  return {
    backgroundColor: color,
    fill: { kind: 'solid', foregroundColor: color, backgroundColor: color },
  }
}

export function ribbonNoFillFormat(): Partial<FormatCellsCellFormat> {
  return {
    backgroundColor: undefined,
    fill: { kind: 'none', foregroundColor: undefined, backgroundColor: undefined, patternKind: 'none' },
  }
}

export function ribbonFontFamilyFormat(family?: string): Partial<FormatCellsCellFormat> {
  return { fontFamily: family, font: { family } }
}

export function ribbonFontSizeFormat(size: unknown): Partial<FormatCellsCellFormat> | undefined {
  const value = Number(size)
  return Number.isFinite(value) ? { fontSize: value, font: { size: value } } : undefined
}

export function ribbonMergeCenterFormat(): Partial<FormatCellsCellFormat> {
  return { align: 'center', alignment: { horizontal: 'center' } }
}

export function ribbonTextStyleCommandFormat(
  id: RibbonTextStyleCommandId,
  enabled: boolean,
): Partial<FormatCellsCellFormat> {
  if (id === 'bold') {
    return { bold: enabled, font: { bold: enabled } }
  }
  if (id === 'italic') {
    return { italic: enabled, font: { italic: enabled } }
  }
  return { underline: enabled, font: { underline: enabled } }
}

export function ribbonHorizontalAlignmentFormat(
  horizontal: RibbonHorizontalAlignment,
): Partial<FormatCellsCellFormat> {
  return { align: horizontal, alignment: { horizontal } }
}

function horizontalAlignmentForCommand(id: RibbonHorizontalAlignmentCommandId): RibbonHorizontalAlignment {
  if (id === 'align-left') return 'left'
  if (id === 'align-center') return 'center'
  return 'right'
}

function normalizedRibbonCount(value: unknown): number {
  const next = Math.floor(Number(value))
  return Number.isFinite(next) ? Math.max(0, next) : 0
}

export function currentRibbonIndent(format?: FormatCellsCellFormat): number {
  return normalizedRibbonCount(format?.indent ?? formatCellsSection(format ?? {}, 'alignment').indent)
}

export function ribbonIndentCommandFormat(
  id: RibbonIndentCommandId,
  format?: FormatCellsCellFormat,
): Partial<FormatCellsCellFormat> {
  const current = currentRibbonIndent(format)
  const indent = id === 'increase-indent'
    ? Math.min(15, current + 1)
    : Math.max(0, current - 1)
  return { indent, alignment: { indent } }
}

export function currentRibbonWrapText(format?: FormatCellsCellFormat): boolean {
  const alignment = formatCellsSection(format ?? {}, 'alignment')
  return Boolean(format?.wrapText || alignment.wrapText)
}

export function ribbonWrapTextCommandFormat(
  format?: FormatCellsCellFormat,
): Partial<FormatCellsCellFormat> {
  const wrapText = !currentRibbonWrapText(format)
  return { wrapText, alignment: { wrapText } }
}

export function ribbonDecimalCommandFormat(
  id: RibbonDecimalCommandId,
  currentNumberFormat?: Record<string, unknown>,
): Partial<FormatCellsCellFormat> {
  const current = normalizedRibbonCount(currentNumberFormat?.decimalPlaces)
  const decimalPlaces = id === 'increase-decimal'
    ? Math.min(30, current + 1)
    : Math.max(0, current - 1)
  return {
    numberFormat: {
      ...currentNumberFormat,
      decimalPlaces,
    },
  }
}

function cloneFormatValue<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => cloneFormatValue(entry)) as T
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      out[key] = cloneFormatValue(entry)
    }
    return out as T
  }
  return value
}

export function ribbonFormatPatchFromDescriptor(
  descriptor: RibbonFormatDescriptor,
): Partial<FormatCellsCellFormat> {
  return cloneFormatValue(descriptor.format)
}

export function ribbonTableStylePatchFromDescriptor(
  descriptor: RibbonTableStyleDescriptor,
): RibbonTableStyleDescriptor['style'] {
  return cloneFormatValue(descriptor.style)
}

export function ribbonTableStyleIdFromDescriptor(
  descriptor: RibbonTableStyleDescriptor,
): string {
  return String(descriptor.tableStyleId ?? '').trim()
}

export function ribbonFormatPatchFromMenuDescriptor(
  descriptor: RibbonMenuDescriptor,
): Partial<FormatCellsCellFormat> | undefined {
  switch (descriptor.action) {
    case 'set-font-family':
      return ribbonFontFamilyFormat(typeof descriptor.value === 'string' ? descriptor.value : undefined)
    case 'set-font-size':
      return ribbonFontSizeFormat(descriptor.value)
    case 'set-font-color':
      return typeof descriptor.value === 'string' ? ribbonFontColorFormat(descriptor.value) : undefined
    case 'set-fill-color':
      return typeof descriptor.value === 'string' ? ribbonFillColorFormat(descriptor.value) : undefined
    case 'set-number-format': {
      const numberFormat = RIBBON_NUMBER_FORMAT_PRESETS.find((preset) => preset.id === descriptor.value)
      return numberFormat ? ribbonFormatPatchFromDescriptor(numberFormat) : undefined
    }
    case 'set-border-format': {
      const borderFormat = RIBBON_BORDER_FORMAT_PRESETS.find((preset) => preset.id === descriptor.value)
      return borderFormat ? ribbonFormatPatchFromDescriptor(borderFormat) : undefined
    }
    case 'set-cell-style': {
      const cellStyle = RIBBON_CELL_STYLE_PRESETS.find((preset) => preset.id === descriptor.value)
      return cellStyle ? ribbonFormatPatchFromDescriptor(cellStyle) : undefined
    }
    case 'no-fill':
      return ribbonNoFillFormat()
    default:
      return undefined
  }
}

export function ribbonDefaultBorderFormat(): Partial<FormatCellsCellFormat> | undefined {
  const bottomBorderPreset = RIBBON_BORDER_FORMAT_PRESETS.find((entry) => entry.id === 'bottom')
  return bottomBorderPreset ? ribbonFormatPatchFromDescriptor(bottomBorderPreset) : undefined
}

export function ribbonDefaultTableStylePatch(): RibbonTableStyleDescriptor['style'] | undefined {
  const defaultTableStyle = RIBBON_TABLE_STYLE_PRESETS.find((entry) => entry.id === 'green')
  return defaultTableStyle ? ribbonTableStylePatchFromDescriptor(defaultTableStyle) : undefined
}

export function ribbonDefaultTableStyleId(): string | undefined {
  const defaultTableStyle = RIBBON_TABLE_STYLE_PRESETS.find((entry) => entry.id === 'green')
  const styleId = defaultTableStyle ? ribbonTableStyleIdFromDescriptor(defaultTableStyle) : ''
  return styleId || undefined
}

export function ribbonTableStylePatchFromMenuDescriptor(
  descriptor: RibbonMenuDescriptor,
): RibbonTableStyleDescriptor['style'] | undefined {
  if (descriptor.action !== 'set-table-style') return undefined
  const tableStyle = RIBBON_TABLE_STYLE_PRESETS.find((entry) => entry.id === descriptor.value)
  return tableStyle ? ribbonTableStylePatchFromDescriptor(tableStyle) : undefined
}

export function ribbonTableStyleIdFromMenuDescriptor(
  descriptor: RibbonMenuDescriptor,
): string | undefined {
  if (descriptor.action !== 'set-table-style') return undefined
  const tableStyle = RIBBON_TABLE_STYLE_PRESETS.find((entry) => entry.id === descriptor.value)
  const styleId = tableStyle ? ribbonTableStyleIdFromDescriptor(tableStyle) : ''
  return styleId || undefined
}

export function buildRibbonMenuActionPlan(
  descriptor: RibbonMenuDescriptor,
): RibbonFormatActionPlan | undefined {
  const format = ribbonFormatPatchFromMenuDescriptor(descriptor)
  if (format) return { action: 'apply-format', format }

  const tableStyle = ribbonTableStylePatchFromMenuDescriptor(descriptor)
  const tableStyleId = ribbonTableStyleIdFromMenuDescriptor(descriptor)
  if (tableStyle && tableStyleId) return { action: 'apply-table-style', styleId: tableStyleId, style: tableStyle }

  switch (descriptor.action) {
    case 'open-format-cells':
      return { action: 'open-format-cells' }
    case 'autofit-column-width':
      return { action: 'autofit-column-width' }
    case 'autofit-row-height':
      return { action: 'autofit-row-height' }
    case 'clear-formats':
      return { action: 'clear-formats' }
    case 'sort-ascending':
      return { action: 'sort-selection', descending: false }
    case 'sort-descending':
      return { action: 'sort-selection', descending: true }
    case 'filter-selection':
      return { action: 'apply-autofilter' }
    case 'clear-filter':
      return { action: 'clear-autofilter' }
    case 'merge-center':
      return { action: 'merge-center', format: ribbonMergeCenterFormat() }
    case 'merge-across':
      return { action: 'merge-across' }
    case 'unmerge':
      return { action: 'unmerge' }
    default:
      return undefined
  }
}

export function ribbonNumberCommandFormat(
  id: RibbonNumberCommandId,
  currentNumberFormat?: Record<string, unknown>,
): Partial<FormatCellsCellFormat> {
  if (id === 'number-format') {
    const general = RIBBON_NUMBER_FORMAT_PRESETS.find((entry) => entry.id === 'general')
    return general
      ? ribbonFormatPatchFromDescriptor(general)
      : { numberFormat: { category: 'general' } }
  }
  if (id === 'accounting-format') {
    return { numberFormat: { category: 'accounting', decimalPlaces: 2, currencySymbol: '$' } }
  }
  if (id === 'percent-style') {
    return { numberFormat: { category: 'percentage', decimalPlaces: 0 } }
  }
  return {
    numberFormat: {
      category: 'number',
      decimalPlaces: currentNumberFormat?.decimalPlaces ?? 2,
      useThousandsSeparator: true,
    },
  }
}

export function buildRibbonColorInputActionPlan(args: {
  kind: RibbonColorInputKind
  color: string
}): RibbonFormatActionPlan {
  return {
    action: 'apply-format',
    format: args.kind === 'font'
      ? ribbonFontColorFormat(args.color)
      : ribbonFillColorFormat(args.color),
  }
}

export function buildRibbonFormatCommandActionPlan(args: {
  commandId: string
  currentFormat?: FormatCellsCellFormat
  currentBold?: boolean
  currentItalic?: boolean
  currentUnderline?: boolean
}): RibbonFormatActionPlan | undefined {
  switch (args.commandId) {
    case 'bold':
      return { action: 'apply-format', format: ribbonTextStyleCommandFormat('bold', !args.currentBold) }
    case 'italic':
      return { action: 'apply-format', format: ribbonTextStyleCommandFormat('italic', !args.currentItalic) }
    case 'underline':
      return { action: 'apply-format', format: ribbonTextStyleCommandFormat('underline', !args.currentUnderline) }
    case 'borders': {
      const format = ribbonDefaultBorderFormat()
      return format ? { action: 'apply-format', format } : undefined
    }
    case 'align-left':
    case 'align-center':
    case 'align-right':
      return {
        action: 'apply-format',
        format: ribbonHorizontalAlignmentFormat(horizontalAlignmentForCommand(args.commandId)),
      }
    case 'decrease-indent':
    case 'increase-indent':
      return {
        action: 'apply-format',
        format: ribbonIndentCommandFormat(args.commandId, args.currentFormat),
      }
    case 'wrap-text':
      return {
        action: 'apply-format',
        format: ribbonWrapTextCommandFormat(args.currentFormat),
      }
    case 'merge-center':
      return { action: 'merge-center', format: ribbonMergeCenterFormat() }
    case 'clear-formats':
      return { action: 'clear-formats' }
    case 'number-format':
    case 'accounting-format':
    case 'percent-style':
    case 'comma-style':
      return {
        action: 'apply-format',
        format: ribbonNumberCommandFormat(args.commandId, args.currentFormat?.numberFormat),
      }
    case 'increase-decimal':
    case 'decrease-decimal':
      return {
        action: 'apply-format',
        format: ribbonDecimalCommandFormat(args.commandId, args.currentFormat?.numberFormat),
      }
    case 'format-as-table': {
      const style = ribbonDefaultTableStylePatch()
      const styleId = ribbonDefaultTableStyleId()
      return style && styleId ? { action: 'apply-table-style', styleId, style } : undefined
    }
    case 'format-cells':
      return { action: 'open-format-cells' }
    default:
      return undefined
  }
}

export function toFormatCellsColorInputValue(value: unknown, fallback: string): string {
  const text = String(value || '').trim()
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback
}

export function formatCellsSection(
  format: FormatCellsCellFormat,
  key: FormatCellsStyleSectionKey,
): Record<string, unknown> {
  const value = format[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

export function mergeFormatCellsDraft(
  base: FormatCellsCellFormat,
  patch: Partial<FormatCellsCellFormat>,
): FormatCellsCellFormat {
  return {
    ...base,
    ...patch,
    ...(patch.numberFormat ? { numberFormat: { ...base.numberFormat, ...patch.numberFormat } } : null),
    ...(patch.font ? { font: { ...base.font, ...patch.font } } : null),
    ...(patch.fill ? { fill: { ...base.fill, ...patch.fill } } : null),
    ...(patch.border ? { border: { ...base.border, ...patch.border } } : null),
    ...(patch.alignment ? { alignment: { ...base.alignment, ...patch.alignment } } : null),
    ...(patch.protection ? { protection: { ...base.protection, ...patch.protection } } : null),
  }
}

export function numberFormatPresetForCategory(
  category: string,
  currentNumberFormat?: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = String(category || 'general')
  const presets: Record<string, Record<string, unknown>> = {
    general: { category: normalized },
    number: { category: normalized, decimalPlaces: 2 },
    currency: { category: normalized, decimalPlaces: 2, currencySymbol: '$', useThousandsSeparator: true },
    accounting: { category: normalized, decimalPlaces: 2, currencySymbol: '$', useThousandsSeparator: true },
    date: { category: normalized, code: 'm/d/yyyy' },
    time: { category: normalized, code: 'h:mm AM/PM' },
    percentage: { category: normalized, decimalPlaces: 0 },
    fraction: { category: normalized, code: '# ?/?' },
    scientific: { category: normalized, decimalPlaces: 2 },
    text: { category: normalized },
    special: { category: normalized, code: '00000' },
    custom: { category: normalized, code: String(currentNumberFormat?.code ?? '') },
  }
  return presets[normalized] ?? { category: normalized }
}

function numberWithDecimals(value: number, decimals: number, thousands: boolean): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: thousands,
  }).format(value)
}

function formatFractionSample(code: string): string {
  if (code.includes('???')) return '1 1/250'
  if (code.includes('??')) return '1 1/25'
  return '1 1/4'
}

function formatSpecialSample(code: string): string {
  if (code === '00000-0000') return '02139-0007'
  if (code === '(000) 000-0000') return '(555) 010-2400'
  if (code === '000-00-0000') return '123-45-6789'
  return '02139'
}

export function formatCustomNumberSample(code: string): string {
  const trimmed = code.trim()
  if (!trimmed) return '1234.56'
  const firstSection = trimmed.split(';')[0] ?? trimmed
  if (firstSection.includes('@')) return firstSection.replace(/@/g, 'Text')
  if (/[ymdhHsS]/.test(firstSection)) {
    if (firstSection.includes('mmmm')) return 'March 14, 2012'
    if (firstSection.includes('mmm')) return '14-Mar-12'
    if (firstSection.includes('AM/PM')) return '1:30 PM'
    return '3/14/2012'
  }
  if (firstSection.includes('%')) return '12.35%'
  if (/[?]\s*[/]\s*[?]/.test(firstSection)) return formatFractionSample(firstSection)
  if (/e[+-]?0/i.test(firstSection)) return '1.23E+03'
  if (firstSection.includes('$')) return '$1,234.56'
  if (firstSection.includes(',')) return '1,234.56'
  if (firstSection.includes('.')) {
    const decimals = Math.min(10, (firstSection.split('.')[1] ?? '').replace(/[^0#?]/g, '').length || 2)
    return numberWithDecimals(1234.56, decimals, false)
  }
  return '1235'
}

export function formatNumberSample(format: Record<string, unknown>): string {
  const category = String(format.category ?? 'general').toLowerCase()
  const code = String(format.code ?? '')
  const decimals = Math.max(0, Math.min(30, Number(format.decimalPlaces ?? (category === 'percentage' ? 0 : 2)) || 0))
  const thousands = format.useThousandsSeparator === true || ['currency', 'accounting'].includes(category)
  const symbol = String(format.currencySymbol ?? '$')
  switch (category) {
    case 'number':
      return numberWithDecimals(1234.56, decimals, thousands)
    case 'currency':
      return `${symbol}${numberWithDecimals(1234.56, decimals, thousands)}`
    case 'accounting':
      return `${symbol} ${numberWithDecimals(1234.56, decimals, true)}`
    case 'date':
      if (code.includes('mmmm')) return 'March 14, 2012'
      if (code.includes('mmm')) return '14-Mar-12'
      return '3/14/2012'
    case 'time':
      if (code.includes('[h]')) return '37:30:55'
      if (code.includes(':ss')) return code.includes('AM/PM') ? '1:30:55 PM' : '13:30:55'
      return code.includes('AM/PM') ? '1:30 PM' : '13:30'
    case 'percentage':
      return `${numberWithDecimals(12.3456, decimals, false)}%`
    case 'fraction':
      return formatFractionSample(code || '# ?/?')
    case 'scientific':
      return `1.${'23'.slice(0, Math.max(0, Math.min(2, decimals))).padEnd(decimals, '0')}E+03`
    case 'text':
      return '1234.56'
    case 'special':
      return formatSpecialSample(code || '00000')
    case 'custom':
      return formatCustomNumberSample(code)
    default:
      return '1234.56'
  }
}

export function borderCssStyle(style: unknown): string {
  const value = String(style ?? '').trim()
  if (value === 'none') return 'none'
  if (value === 'double') return 'double'
  if (value === 'dotted' || value === 'hair') return 'dotted'
  if (value.includes('Dash') || value.includes('dash') || value === 'dashed') return 'dashed'
  return 'solid'
}

export function borderCssWidth(style: unknown): string {
  const value = String(style ?? '').trim()
  if (value === 'none') return '0'
  if (value === 'thick' || value === 'double') return '3px'
  if (value === 'medium' || value.startsWith('medium')) return '2px'
  if (value === 'hair') return '1px'
  return '1px'
}

export function previewBorderSide(side: unknown): string | undefined {
  if (!side || typeof side !== 'object' || Array.isArray(side)) return undefined
  const record = side as Record<string, unknown>
  if (record.style === 'none') return '0 solid transparent'
  return `${borderCssWidth(record.style)} ${borderCssStyle(record.style)} ${String(record.color || '#1f2937')}`
}

export function buildFormatCellsPreviewStyle(draft: FormatCellsCellFormat): FormatCellsPreviewStyle {
  const font = formatCellsSection(draft, 'font')
  const fill = formatCellsSection(draft, 'fill')
  const border = formatCellsSection(draft, 'border')
  const alignment = formatCellsSection(draft, 'alignment')
  const style: FormatCellsPreviewStyle = {
    color: String(font.color ?? draft.textColor ?? '#111827'),
    backgroundColor: String(fill.foregroundColor ?? fill.backgroundColor ?? draft.backgroundColor ?? '#ffffff'),
    fontFamily: String(font.family ?? draft.fontFamily ?? 'inherit'),
    fontSize: Number(font.size ?? draft.fontSize ?? 13) || 13,
    fontWeight: (font.bold ?? draft.bold) ? 800 : 500,
    fontStyle: (font.italic ?? draft.italic) ? 'italic' : 'normal',
    textDecoration: [
      (font.underline ?? draft.underline) ? 'underline' : '',
      (font.strikethrough ?? draft.strikethrough) ? 'line-through' : '',
    ].filter(Boolean).join(' ') || 'none',
    textAlign: ['left', 'center', 'right', 'justify'].includes(String(alignment.horizontal ?? draft.align))
      ? String(alignment.horizontal ?? draft.align)
      : 'left',
    direction: alignment.readingOrder === 'rightToLeft' ? 'rtl' : alignment.readingOrder === 'leftToRight' ? 'ltr' : 'inherit',
  }
  const top = previewBorderSide(border.top)
  const right = previewBorderSide(border.right)
  const bottom = previewBorderSide(border.bottom)
  const left = previewBorderSide(border.left)
  if (top) style.borderTop = top
  if (right) style.borderRight = right
  if (bottom) style.borderBottom = bottom
  if (left) style.borderLeft = left
  if (!top && !right && !bottom && !left) style.border = '1px solid #cfd5dd'
  return style
}

export function getActiveBorderSection(border: Record<string, unknown>, activeBorderSide: string): Record<string, unknown> {
  if (activeBorderSide === 'inside') {
    return formatCellsSection({ border: (border.insideHorizontal ?? border.insideVertical) as Record<string, unknown> }, 'border')
  }
  if (activeBorderSide === 'diagonal') {
    return formatCellsSection({ border: (border.diagonalUp ?? border.diagonalDown ?? border.diagonal) as Record<string, unknown> }, 'border')
  }
  return formatCellsSection({ border: border[activeBorderSide] as Record<string, unknown> }, 'border')
}

export function borderPatchForActiveSide(
  activeBorderSide: string,
  nextSide: Record<string, unknown>,
): Record<string, unknown> {
  if (activeBorderSide === 'inside') {
    return { activeSide: activeBorderSide, insideHorizontal: nextSide, insideVertical: nextSide }
  }
  if (activeBorderSide === 'diagonal') {
    return { activeSide: activeBorderSide, diagonalUp: nextSide, diagonalDown: nextSide }
  }
  return { activeSide: activeBorderSide, [activeBorderSide]: nextSide }
}

export function buildBorderPresetPatch(args: {
  preset: FormatCellsBorderPreset
  activeBorderSide: string
  activeBorder: Record<string, unknown>
}): Record<string, unknown> {
  const side = {
    style: String(args.activeBorder.style ?? 'thin'),
    color: String(args.activeBorder.color ?? '#808080'),
  }
  const thickSide = { ...side, style: 'thick' }
  const doubleSide = { ...side, style: 'double' }
  if (args.preset === 'none') {
    return {
      activeSide: args.activeBorderSide,
      top: { style: 'none' },
      right: { style: 'none' },
      bottom: { style: 'none' },
      left: { style: 'none' },
      insideHorizontal: { style: 'none' },
      insideVertical: { style: 'none' },
      diagonalUp: { style: 'none' },
      diagonalDown: { style: 'none' },
    }
  }
  if (args.preset === 'all') {
    return {
      activeSide: args.activeBorderSide,
      top: side,
      right: side,
      bottom: side,
      left: side,
      insideHorizontal: side,
      insideVertical: side,
    }
  }
  if (args.preset === 'outline') {
    return {
      activeSide: args.activeBorderSide,
      top: side,
      right: side,
      bottom: side,
      left: side,
    }
  }
  if (args.preset === 'thickOutline') {
    return {
      activeSide: args.activeBorderSide,
      top: thickSide,
      right: thickSide,
      bottom: thickSide,
      left: thickSide,
    }
  }
  if (args.preset === 'inside') {
    return {
      activeSide: args.activeBorderSide,
      insideHorizontal: side,
      insideVertical: side,
    }
  }
  if (args.preset === 'bottom') {
    return { activeSide: args.activeBorderSide, bottom: side }
  }
  if (args.preset === 'topBottom') {
    return { activeSide: args.activeBorderSide, top: side, bottom: side }
  }
  if (args.preset === 'topThickBottom') {
    return { activeSide: args.activeBorderSide, top: side, bottom: thickSide }
  }
  return { activeSide: args.activeBorderSide, top: side, bottom: doubleSide }
}
